import { useCallback, useRef } from "react";
import type { TextInput } from "react-native";

// A row of fields the keyboard walks through: the "next" key on field i
// focuses field i + 1, the last one says "done" and closes the keyboard
// (editor v3 brief §7.2, #336). Order is the index the caller hands out;
// a field that is not mounted is skipped. Validation stays with each field
// (numericFieldErrorText): the chain only moves the focus.
//
// ⚠️ Both submit props, on purpose: RN reads `submitBehavior` (it wins over
// `blurOnSubmit`), react-native-web only knows `blurOnSubmit`. Keeping the
// keyboard up between fields is what stops it from closing and reopening.

/** The last field, or a field alone: Return says «done» and closes the
 *  keyboard. The empty `onSubmitEditing` is what makes RNW keep a multiline
 *  field's Enter from inserting a line break. */
export const DONE_FIELD_PROPS = {
  returnKeyType: "done",
  submitBehavior: "blurAndSubmit",
  blurOnSubmit: true,
  onSubmitEditing: () => {},
} as const;

export type FocusChainFieldProps = {
  ref: (el: TextInput | null) => void;
  returnKeyType: "next" | "done";
  submitBehavior: "submit" | "blurAndSubmit";
  blurOnSubmit: boolean;
  onSubmitEditing: () => void;
  /** Present only when the caller asked for it (`onFocus` argument). */
  onFocus?: () => void;
};

export function useFocusChain(
  count: number,
  onFocus?: () => void,
): {
  /** Spread onto field `i` (a TextField or TextInput). */
  fieldProps: (i: number) => FocusChainFieldProps;
  /** Focus field `i`, or the first mounted one after it. False: none is. */
  focusAt: (i: number) => boolean;
} {
  const refs = useRef<(TextInput | null)[]>([]);

  const focusAt = useCallback(
    (i: number) => {
      for (let j = i; j < count; j += 1) {
        const el = refs.current[j];
        if (el) {
          el.focus();
          return true;
        }
      }
      return false;
    },
    [count],
  );

  const fieldProps = useCallback(
    (i: number): FocusChainFieldProps => {
      const last = i >= count - 1;
      return {
        ref: (el) => {
          refs.current[i] = el;
        },
        ...(last
          ? DONE_FIELD_PROPS
          : {
              returnKeyType: "next" as const,
              submitBehavior: "submit" as const,
              blurOnSubmit: false,
              // Nothing mounted after it: behave as the last field.
              onSubmitEditing: () => {
                if (!focusAt(i + 1)) refs.current[i]?.blur();
              },
            }),
        ...(onFocus ? { onFocus } : {}),
      };
    },
    [count, focusAt, onFocus],
  );

  return { fieldProps, focusAt };
}
